// ---------------------------------------------------------------------------
// FocusSessionHost — quem RENDERIZA o Modo Foco.
//
// Mora no `AppFrame`, ao lado dos watchers de celebração, e pelo mesmo motivo
// que eles: `FocusModeModal` desenha com `fixed` cru SEM portal, então montado
// fora do frame escaparia dele no desktop.
// ---------------------------------------------------------------------------

import { lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useFocusSessionHostChannel } from "./focusSession";

const FocusModeModal = lazy(() =>
  import("../components/FocusModeModal").then((m) => ({ default: m.FocusModeModal })),
);

const BookCompletionModal = lazy(() =>
  import("../../books/components/BookCompletionModal").then((m) => ({
    default: m.BookCompletionModal,
  })),
);

/**
 * Renderiza o Modo Foco. Mora no `AppFrame` — ver o cabeçalho deste arquivo.
 */
export function FocusSessionHost() {
  const host = useFocusSessionHostChannel();
  const { user } = useAuth();
  const { pathname } = useLocation();

  if (!host || !user) return null;

  /**
   * Livro concluído pelo Foco (BUG-304): a mesma tela de nota e compartilhar
   * que o "Terminei" manual abre. Sem badges na mão de propósito — desde o
   * BUG-281 quem as concede é o servidor, e o watcher as anuncia quando esta
   * tela fechar (ela suprime celebração enquanto está aberta).
   */
  const finished = host.finishedBook?.userId === user.uid ? host.finishedBook : null;
  const completion = finished ? (
    <Suspense fallback={null}>
      <BookCompletionModal
        userBookId={finished.id}
        title={finished.title}
        author={finished.author}
        coverUrl={finished.coverUrl}
        newBadges={[]}
        onClose={host.dismissFinishedBook}
      />
    </Suspense>
  ) : null;

  if (!host.request) return completion;
  /**
   * O drawer minimizado se ancora ACIMA da BottomNav (`--nav-h`), e o
   * onboarding é a única rota emoldurada que não a renderiza — lá ele flutuaria
   * 60px acima da borda. É inalcançável na prática (quem está no wizard não tem
   * estante para focar), então a guarda é honestidade sobre a premissa, não um
   * caso que alguém vá encontrar.
   */
  if (pathname.startsWith("/onboarding")) return null;

  const { request } = host;

  return (
    <Suspense fallback={null}>
      <FocusModeModal
        isOpen
        userId={user.uid}
        bookId={request.bookId}
        bookTitle={request.title}
        bookCoverUrl={request.coverUrl ?? undefined}
        currentPages={request.currentPages ?? 0}
        totalPages={request.totalPages || undefined}
        bookFormat={request.format ?? "physical"}
        userBook={request.book}
        autoStartTarget={request.target}
        recoveredSession={request.session ?? null}
        initialMinimized={request.minimized}
        expandSignal={host.expandSignal}
        actionRequest={host.actionRequest}
        onMinimizedChange={host.setMinimized}
        onClose={host.closeFocus}
      />
      {completion}
    </Suspense>
  );
}
